/**
 * 前端操作日志记录系统
 * 用于在控制台详细记录用户操作的每一步，便于调试和问题定位
 */

export enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS'
}

export enum OperationType {
  // 认证相关
  AUTH_LOGIN = 'AUTH_LOGIN',
  AUTH_LOGOUT = 'AUTH_LOGOUT',
  AUTH_CHECK = 'AUTH_CHECK',
  
  // 搜索相关
  SEARCH_START = 'SEARCH_START',
  SEARCH_SUCCESS = 'SEARCH_SUCCESS',
  SEARCH_ERROR = 'SEARCH_ERROR',
  
  // 收藏相关
  FAVORITE_ADD_WORK = 'FAVORITE_ADD_WORK',
  FAVORITE_ADD_EPISODE = 'FAVORITE_ADD_EPISODE',
  FAVORITE_ADD_IMAGE = 'FAVORITE_ADD_IMAGE',
  FAVORITE_REMOVE_WORK = 'FAVORITE_REMOVE_WORK',
  FAVORITE_REMOVE_EPISODE = 'FAVORITE_REMOVE_EPISODE',
  FAVORITE_REMOVE_IMAGE = 'FAVORITE_REMOVE_IMAGE',
  FAVORITE_CHECK_STATUS = 'FAVORITE_CHECK_STATUS',
  FAVORITE_GET_ALL = 'FAVORITE_GET_ALL',
  
  // 下载相关
  DOWNLOAD_START = 'DOWNLOAD_START',
  DOWNLOAD_PROGRESS = 'DOWNLOAD_PROGRESS',
  DOWNLOAD_SUCCESS = 'DOWNLOAD_SUCCESS',
  DOWNLOAD_ERROR = 'DOWNLOAD_ERROR',
  
  // 图片相关
  IMAGE_LOAD = 'IMAGE_LOAD',
  IMAGE_VIEW = 'IMAGE_VIEW',
  IMAGE_PROXY = 'IMAGE_PROXY',
  
  // 剧集相关
  EPISODE_LOAD = 'EPISODE_LOAD',
  EPISODE_VIEW = 'EPISODE_VIEW',
  
  // API请求相关
  API_REQUEST = 'API_REQUEST',
  API_RESPONSE = 'API_RESPONSE',
  API_ERROR = 'API_ERROR',
  
  // 页面导航
  PAGE_NAVIGATE = 'PAGE_NAVIGATE',
  PAGE_LOAD = 'PAGE_LOAD',
  
  // 用户交互
  USER_CLICK = 'USER_CLICK',
  USER_INPUT = 'USER_INPUT',
  USER_SCROLL = 'USER_SCROLL'
}

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  operation: OperationType;
  message: string;
  details?: any;
  duration?: number;
  userId?: string;
  sessionId?: string;
  url?: string;
  userAgent?: string;
}

export interface APILogDetails {
  method: string;
  url: string;
  headers?: Record<string, string>;
  requestData?: any;
  responseStatus?: number;
  responseData?: any;
  responseHeaders?: Record<string, string>;
  duration?: number;
  error?: any;
}

export interface OperationContext {
  userId?: string;
  sessionId?: string;
  pageUrl?: string;
  userAgent?: string;
  timestamp?: string;
}

class OperationLogger {
  private context: OperationContext = {};
  private isEnabled: boolean = true;

  /**
   * 检查日志记录器是否启用
   */
  public getEnabled(): boolean {
    return this.isEnabled;
  }
  private logHistory: LogEntry[] = [];
  private maxHistorySize: number = 1000;
  
  constructor() {
    this.initializeContext();
    this.setupGlobalErrorHandler();
  }
  
  /**
   * 初始化日志上下文
   */
  private initializeContext(): void {
    this.context = {
      sessionId: this.generateSessionId(),
      pageUrl: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString()
    };
    
    // 监听页面变化
    window.addEventListener('popstate', () => {
      this.context.pageUrl = window.location.href;
    });
  }
  
  /**
   * 生成会话ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  
  /**
   * 设置全局错误处理
   */
  private setupGlobalErrorHandler(): void {
    window.addEventListener('error', (event) => {
      this.error(OperationType.API_ERROR, '全局JavaScript错误', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error
      });
    });
    
    window.addEventListener('unhandledrejection', (event) => {
      this.error(OperationType.API_ERROR, '未处理的Promise拒绝', {
        reason: event.reason
      });
    });
  }
  
  /**
   * 设置用户ID
   */
  setUserId(userId: string): void {
    this.context.userId = userId;
    this.info(OperationType.AUTH_CHECK, `设置用户ID: ${userId}`);
  }
  
  /**
   * 启用/禁用日志记录
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    console.log(`🔧 操作日志记录已${enabled ? '启用' : '禁用'}`);
  }
  
  /**
   * 记录日志的核心方法
   */
  private log(level: LogLevel, operation: OperationType, message: string, details?: any, duration?: number): void {
    if (!this.isEnabled) return;
    
    const timestamp = new Date().toISOString();
    const logEntry: LogEntry = {
      timestamp,
      level,
      operation,
      message,
      details,
      duration,
      ...this.context
    };
    
    // 添加到历史记录
    this.logHistory.push(logEntry);
    if (this.logHistory.length > this.maxHistorySize) {
      this.logHistory.shift();
    }
    
    // 控制台输出
    this.outputToConsole(logEntry);
  }
  
  /**
   * 控制台输出格式化
   */
  private outputToConsole(entry: LogEntry): void {
    const timeStr = new Date(entry.timestamp).toLocaleTimeString();
    const emoji = this.getLevelEmoji(entry.level);
    const operationColor = this.getOperationColor(entry.operation);
    
    const baseMessage = `${emoji} [${timeStr}] ${entry.operation}`;
    
    // 根据日志级别选择控制台方法
    const consoleMethod = this.getConsoleMethod(entry.level);
    
    if (entry.details) {
      consoleMethod(
        `%c${baseMessage}%c ${entry.message}`,
        `color: ${operationColor}; font-weight: bold;`,
        'color: inherit;',
        entry.details
      );
    } else {
      consoleMethod(
        `%c${baseMessage}%c ${entry.message}`,
        `color: ${operationColor}; font-weight: bold;`,
        'color: inherit;'
      );
    }
    
    // 如果有持续时间，额外显示
    if (entry.duration !== undefined) {
      console.log(`⏱️ 耗时: ${entry.duration}ms`);
    }
  }
  
  /**
   * 获取日志级别对应的emoji
   */
  private getLevelEmoji(level: LogLevel): string {
    const emojiMap = {
      [LogLevel.DEBUG]: '🔍',
      [LogLevel.INFO]: 'ℹ️',
      [LogLevel.WARN]: '⚠️',
      [LogLevel.ERROR]: '❌',
      [LogLevel.SUCCESS]: '✅'
    };
    return emojiMap[level] || 'ℹ️';
  }
  
  /**
   * 获取操作类型对应的颜色
   */
  private getOperationColor(operation: OperationType): string {
    if (operation.startsWith('AUTH_')) return '#9C27B0'; // 紫色
    if (operation.startsWith('SEARCH_')) return '#2196F3'; // 蓝色
    if (operation.startsWith('FAVORITE_')) return '#E91E63'; // 粉色
    if (operation.startsWith('DOWNLOAD_')) return '#4CAF50'; // 绿色
    if (operation.startsWith('IMAGE_')) return '#FF9800'; // 橙色
    if (operation.startsWith('EPISODE_')) return '#795548'; // 棕色
    if (operation.startsWith('API_')) return '#607D8B'; // 蓝灰色
    if (operation.startsWith('PAGE_')) return '#3F51B5'; // 靛蓝色
    if (operation.startsWith('USER_')) return '#009688'; // 青色
    return '#666666'; // 默认灰色
  }
  
  /**
   * 获取控制台输出方法
   */
  private getConsoleMethod(level: LogLevel): (...args: any[]) => void {
    switch (level) {
      case LogLevel.DEBUG:
        return console.debug;
      case LogLevel.INFO:
      case LogLevel.SUCCESS:
        return console.log;
      case LogLevel.WARN:
        return console.warn;
      case LogLevel.ERROR:
        return console.error;
      default:
        return console.log;
    }
  }
  
  // 公共日志方法
  debug(operation: OperationType, message: string, details?: any): void {
    this.log(LogLevel.DEBUG, operation, message, details);
  }
  
  info(operation: OperationType, message: string, details?: any): void {
    this.log(LogLevel.INFO, operation, message, details);
  }
  
  warn(operation: OperationType, message: string, details?: any): void {
    this.log(LogLevel.WARN, operation, message, details);
  }
  
  error(operation: OperationType, message: string, details?: any): void {
    this.log(LogLevel.ERROR, operation, message, details);
  }
  
  success(operation: OperationType, message: string, details?: any): void {
    this.log(LogLevel.SUCCESS, operation, message, details);
  }
  
  /**
   * 记录API请求开始
   */
  logAPIRequest(method: string, url: string, data?: any, headers?: Record<string, string>): string {
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
    
    this.info(OperationType.API_REQUEST, `${method} ${url}`, {
      requestId,
      method,
      url,
      headers,
      requestData: data,
      timestamp: new Date().toISOString()
    });
    
    return requestId;
  }
  
  /**
   * 记录API响应
   */
  logAPIResponse(requestId: string, status: number, data?: any, headers?: Record<string, string>, duration?: number): void {
    const isSuccess = status >= 200 && status < 300;
    const level = isSuccess ? LogLevel.SUCCESS : LogLevel.ERROR;
    const operation = isSuccess ? OperationType.API_RESPONSE : OperationType.API_ERROR;
    
    this.log(level, operation, `API响应 [${status}]`, {
      requestId,
      status,
      responseData: data,
      responseHeaders: headers,
      duration,
      timestamp: new Date().toISOString()
    }, duration);
  }
  
  /**
   * 记录API错误（增强版）
   */
  logAPIError(requestId: string, error: any, duration?: number): void {
    // 获取调用堆栈
    const stack = new Error().stack;
    
    // 详细的错误信息
    const errorDetails = {
      requestId,
      error: {
        name: error.name,
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
        config: {
          method: error.config?.method,
          url: error.config?.url,
          headers: error.config?.headers,
          data: error.config?.data
        },
        stack: error.stack,
        callStack: stack
      },
      duration,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      pageUrl: window.location.href,
      localStorage: this.getRelevantLocalStorage(),
      sessionStorage: this.getRelevantSessionStorage()
    };
    
    this.error(OperationType.API_ERROR, 'API请求失败', errorDetails);
    
    // 额外的错误分析
    this.analyzeError(error);
  }
  
  /**
   * 获取相关的localStorage数据
   */
  private getRelevantLocalStorage(): Record<string, any> {
    const relevantKeys = ['token', 'user', 'auth', 'favorites'];
    const data: Record<string, any> = {};
    
    relevantKeys.forEach(key => {
      try {
        const value = localStorage.getItem(key);
        if (value) {
          data[key] = key === 'token' ? '[REDACTED]' : JSON.parse(value);
        }
      } catch (e) {
        data[key] = '[PARSE_ERROR]';
      }
    });
    
    return data;
  }
  
  /**
   * 获取相关的sessionStorage数据
   */
  private getRelevantSessionStorage(): Record<string, any> {
    const relevantKeys = ['tempAuth', 'sessionData'];
    const data: Record<string, any> = {};
    
    relevantKeys.forEach(key => {
      try {
        const value = sessionStorage.getItem(key);
        if (value) {
          data[key] = JSON.parse(value);
        }
      } catch (e) {
        data[key] = '[PARSE_ERROR]';
      }
    });
    
    return data;
  }
  
  /**
   * 分析错误类型并提供建议
   */
  private analyzeError(error: any): void {
    const status = error.response?.status;
    let analysis = '';
    let suggestion = '';
    
    switch (status) {
      case 401:
        analysis = '认证失败 - 用户未登录或token已过期';
        suggestion = '请检查登录状态，可能需要重新登录';
        break;
      case 403:
        analysis = '权限不足 - 用户没有访问该资源的权限';
        suggestion = '请检查用户权限或联系管理员';
        break;
      case 404:
        analysis = '资源不存在 - 请求的API端点或资源未找到';
        suggestion = '请检查API路径是否正确';
        break;
      case 429:
        analysis = '请求过于频繁 - 触发了限流机制';
        suggestion = '请稍后重试，或检查请求频率';
        break;
      case 500:
        analysis = '服务器内部错误 - 后端服务出现问题';
        suggestion = '请稍后重试，如果问题持续存在请联系技术支持';
        break;
      default:
        if (!status) {
          analysis = '网络错误 - 无法连接到服务器';
          suggestion = '请检查网络连接或服务器状态';
        } else {
          analysis = `HTTP错误 ${status}`;
          suggestion = '请查看详细错误信息';
        }
    }
    
    this.warn(OperationType.API_ERROR, `错误分析: ${analysis}`, {
      suggestion,
      status,
      timestamp: new Date().toISOString()
    });
  }
  
  /**
   * 记录用户操作
   */
  logUserAction(action: string, target?: string, details?: any): void {
    this.info(OperationType.USER_CLICK, `用户${action}`, {
      action,
      target,
      details,
      pageUrl: window.location.href,
      timestamp: new Date().toISOString()
    });
  }
  
  /**
   * 记录页面导航
   */
  logPageNavigation(from: string, to: string): void {
    this.info(OperationType.PAGE_NAVIGATE, `页面导航: ${from} → ${to}`, {
      from,
      to,
      timestamp: new Date().toISOString()
    });
    
    // 更新上下文
    this.context.pageUrl = to;
  }
  
  /**
   * 记录收藏操作（增强版）
   */
  logFavoriteOperation(type: 'add' | 'remove', itemType: 'work' | 'episode' | 'image', itemId: string, itemTitle?: string): void {
    const operation = type === 'add' 
      ? (itemType === 'work' ? OperationType.FAVORITE_ADD_WORK : 
         itemType === 'episode' ? OperationType.FAVORITE_ADD_EPISODE : OperationType.FAVORITE_ADD_IMAGE)
      : (itemType === 'work' ? OperationType.FAVORITE_REMOVE_WORK : 
         itemType === 'episode' ? OperationType.FAVORITE_REMOVE_EPISODE : OperationType.FAVORITE_REMOVE_IMAGE);
    
    const message = `${type === 'add' ? '添加' : '移除'}${itemType === 'work' ? '作品' : itemType === 'episode' ? '剧集' : '图片'}收藏`;
    
    this.info(operation, message, {
      type,
      itemType,
      itemId,
      itemTitle,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      pageUrl: window.location.href,
      callStack: new Error().stack
    });
  }
  
  /**
   * 开始分步骤操作流程记录
   */
  startStepProcess(processName: string, totalSteps?: number): string {
    const processId = `process_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
    
    this.info(OperationType.USER_CLICK, `开始操作流程: ${processName}`, {
      processId,
      processName,
      totalSteps,
      startTime: new Date().toISOString(),
      pageUrl: window.location.href
    });
    
    return processId;
  }
  
  /**
   * 记录操作步骤
   */
  logStep(processId: string, stepNumber: number, stepName: string, details?: any, status: 'start' | 'success' | 'error' | 'warning' = 'start'): void {
    const levelMap = {
      start: LogLevel.INFO,
      success: LogLevel.SUCCESS,
      error: LogLevel.ERROR,
      warning: LogLevel.WARN
    };
    
    const emoji = {
      start: '🔄',
      success: '✅',
      error: '❌',
      warning: '⚠️'
    };
    
    this.log(levelMap[status], OperationType.USER_CLICK, `${emoji[status]} 步骤 ${stepNumber}: ${stepName}`, {
      processId,
      stepNumber,
      stepName,
      status,
      details,
      timestamp: new Date().toISOString(),
      callStack: new Error().stack
    });
  }
  
  /**
   * 完成操作流程
   */
  finishStepProcess(processId: string, success: boolean, summary?: string): void {
    const level = success ? LogLevel.SUCCESS : LogLevel.ERROR;
    const emoji = success ? '🎉' : '💥';
    
    this.log(level, OperationType.USER_CLICK, `${emoji} 操作流程${success ? '完成' : '失败'}`, {
      processId,
      success,
      summary,
      endTime: new Date().toISOString()
    });
  }
  
  /**
   * 记录详细的认证流程
   */
  logAuthFlow(step: string, details?: any, status: 'start' | 'success' | 'error' = 'start'): void {
    const operation = step.includes('login') ? OperationType.AUTH_LOGIN : 
                     step.includes('logout') ? OperationType.AUTH_LOGOUT : 
                     OperationType.AUTH_CHECK;
    
    const levelMap = {
      start: LogLevel.INFO,
      success: LogLevel.SUCCESS,
      error: LogLevel.ERROR
    };
    
    this.log(levelMap[status], operation, `认证流程: ${step}`, {
      step,
      status,
      details,
      timestamp: new Date().toISOString(),
      localStorage: this.getRelevantLocalStorage(),
      sessionStorage: this.getRelevantSessionStorage(),
      callStack: new Error().stack
    });
  }
  
  /**
   * 记录下载操作
   */
  logDownloadOperation(type: 'start' | 'progress' | 'success' | 'error', details?: any): void {
    const operationMap = {
      start: OperationType.DOWNLOAD_START,
      progress: OperationType.DOWNLOAD_PROGRESS,
      success: OperationType.DOWNLOAD_SUCCESS,
      error: OperationType.DOWNLOAD_ERROR
    };
    
    const levelMap = {
      start: LogLevel.INFO,
      progress: LogLevel.DEBUG,
      success: LogLevel.SUCCESS,
      error: LogLevel.ERROR
    };
    
    this.log(levelMap[type], operationMap[type], `下载${type}`, {
      ...details,
      timestamp: new Date().toISOString()
    });
  }
  
  /**
   * 获取日志历史
   */
  getLogHistory(filter?: { level?: LogLevel; operation?: OperationType; limit?: number }): LogEntry[] {
    let filtered = this.logHistory;
    
    if (filter?.level) {
      filtered = filtered.filter(entry => entry.level === filter.level);
    }
    
    if (filter?.operation) {
      filtered = filtered.filter(entry => entry.operation === filter.operation);
    }
    
    if (filter?.limit) {
      filtered = filtered.slice(-filter.limit);
    }
    
    return filtered;
  }
  
  /**
   * 导出日志
   */
  exportLogs(format: 'json' | 'csv' = 'json'): string {
    if (format === 'json') {
      return JSON.stringify(this.logHistory, null, 2);
    } else {
      // CSV格式
      const headers = ['timestamp', 'level', 'operation', 'message', 'userId', 'sessionId'];
      const csvRows = [headers.join(',')];
      
      this.logHistory.forEach(entry => {
        const row = [
          entry.timestamp,
          entry.level,
          entry.operation,
          `"${entry.message.replace(/"/g, '""')}"`,
          entry.userId || '',
          entry.sessionId || ''
        ];
        csvRows.push(row.join(','));
      });
      
      return csvRows.join('\n');
    }
  }
  
  /**
   * 清除日志历史
   */
  clearHistory(): void {
    this.logHistory = [];
    this.info(OperationType.USER_CLICK, '清除日志历史');
  }
  
  /**
   * 显示日志统计
   */
  showStats(): void {
    const stats = {
      总日志数: this.logHistory.length,
      按级别统计: {} as Record<string, number>,
      按操作类型统计: {} as Record<string, number>,
      会话ID: this.context.sessionId,
      用户ID: this.context.userId || '未设置'
    };
    
    // 统计级别
    this.logHistory.forEach(entry => {
      stats.按级别统计[entry.level] = (stats.按级别统计[entry.level] || 0) + 1;
      stats.按操作类型统计[entry.operation] = (stats.按操作类型统计[entry.operation] || 0) + 1;
    });
    
    console.group('📊 操作日志统计');
    console.table(stats);
    console.groupEnd();
  }
}

// 创建全局实例
export const operationLogger = new OperationLogger();

// 在开发环境下暴露到全局对象，方便调试
if (process.env.NODE_ENV === 'development') {
  (window as any).operationLogger = operationLogger;
  console.log('🔧 操作日志记录器已启用，可通过 window.operationLogger 访问');
  console.log('🔧 常用命令:');
  console.log('  - operationLogger.showStats() - 显示统计信息');
  console.log('  - operationLogger.getLogHistory() - 获取日志历史');
  console.log('  - operationLogger.exportLogs() - 导出日志');
  console.log('  - operationLogger.clearHistory() - 清除历史');
  console.log('  - operationLogger.setEnabled(false) - 禁用日志');
}

export default operationLogger;